import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Play, Crown } from "lucide-react";
import { useAuth, isSubscribed } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/branding";

export default function ShowDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const subscribed = isSubscribed(user);
  const [episodes, setEpisodes] = useState([]);

  useEffect(() => {
    api.get("/episodes", { params: { show: "Boyz Gone Wild" } }).then(r => setEpisodes(r.data)).catch(() => {});
  }, [slug]);

  return (
    <div className="min-h-screen bg-brand-bg text-white">
      <Navbar />
      <section className="relative h-[62vh] overflow-hidden">
        <div className="absolute inset-0" style={{
          background:
            "radial-gradient(60% 80% at 80% 20%, rgba(255,0,85,0.25) 0%, transparent 60%)," +
            "linear-gradient(180deg, #08020a 0%, #050505 70%, #050505 100%)",
        }} />
        <div className="absolute inset-0 hero-grad" />
        <div className="absolute inset-y-0 right-0 w-[55%] opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: "repeating-linear-gradient(115deg, #fff 0 1px, transparent 1px 18px)" }} />

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-14">
          <div className="flex items-center gap-3 mb-3">
            <img src={LOGO_URL} alt="ball0ut.ent" className="h-12 w-12 object-contain" />
            <div className="text-xs uppercase tracking-[0.3em] text-brand-primary">ball0ut.ent</div>
          </div>
          <h1 className="font-display text-6xl md:text-8xl uppercase tracking-tight">Boyz Gone Wild</h1>
          <p className="text-brand-muted max-w-2xl mt-4">
            The wildest, most unfiltered boyz in one location. No rules. No limits. All on footage.
          </p>
          <div className="mt-6 flex gap-3 flex-wrap">
            {!subscribed && user?.role !== "admin" ? (
              <Link to="/subscribe" data-testid="show-subscribe-btn">
                <Button className="bg-brand-primary hover:bg-brand-primaryHover rounded-none px-7 h-12 uppercase tracking-[0.15em]">
                  <Crown className="h-4 w-4 mr-2" /> Subscribe · $6.99/mo
                </Button>
              </Link>
            ) : (
              episodes[0] && (
                <Link to={`/watch/${episodes[0].id}`}>
                  <Button className="bg-brand-primary hover:bg-brand-primaryHover rounded-none px-7 h-12 uppercase tracking-[0.15em]">
                    <Play className="h-4 w-4 mr-2 fill-white" /> Play Episode 1
                  </Button>
                </Link>
              )
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-16">
        <div className="text-xs uppercase tracking-[0.3em] text-brand-primary mb-3">Season 1</div>
        <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-8">All episodes</h2>
        <div className="space-y-4">
          {episodes.map((ep, i) => (
            <Link key={ep.id} to={`/watch/${ep.id}`} className="group grid grid-cols-1 md:grid-cols-[280px_1fr_auto] gap-5 items-center border border-white/10 hover:border-brand-primary p-3 transition" data-testid={`show-episode-${i}`}>
              <div className="aspect-[16/9] overflow-hidden bg-brand-surface relative">
                {ep.thumbnail_url ? (
                  <img src={ep.thumbnail_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center" style={{
                    background: "radial-gradient(120% 100% at 0% 0%, rgba(255,0,85,0.25) 0%, rgba(5,5,5,1) 60%), linear-gradient(135deg, #1a0410 0%, #050505 100%)"
                  }}>
                    <div className="font-display text-2xl uppercase text-white/85">EP {ep.episode_number}</div>
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-brand-primary">Episode {ep.episode_number}</div>
                <div className="font-display text-2xl uppercase tracking-tight mt-1">{ep.title || "TBA"}</div>
                <div className="text-sm text-brand-muted mt-2 line-clamp-2 max-w-xl">{ep.description || "TBA"}</div>
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-brand-muted hidden md:block">
                {subscribed || user?.role === "admin" ? "Stream" : "Premium"}
              </div>
            </Link>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
